import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Upload, FileText, AlertCircle, Download } from 'lucide-react';
import { useCreateBulkOrganisations } from '@/services/organisations/queries';
import { useAuthStore } from '@/store/auth';
import { showToast } from '@/components/ui/show-toast';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';

interface ParsedOrganisation {
  name: string;
  email: string;
  workspaceIds: string[];
  phoneNumber?: string;
  address?: string;
  website?: string;
  linkedInUrl?: string;
  headOfficeLocation?: string;
  bio?: string;
  mission?: string;
  yearFounded?: string;
  contactPerson: {
    name: string;
    email: string;
    bio?: string;
    linkedInUrl?: string;
  };
  map: {
    category: string;
    tags: string[];
  }[];
}

interface BulkUploadModalProps {
  onSuccess?: () => void;
}

interface BulkUploadResult {
  success: boolean;
  error?: string;
  name: string;
}

interface BulkUploadResponse {
  successful: number;
  failed: number;
  results: BulkUploadResult[];
}

export function BulkUploadModal({ onSuccess }: BulkUploadModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedOrganisation[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [uploadResults, setUploadResults] = useState<BulkUploadResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const activeWorkspace = useAuthStore(state => state.activeWorkspace);

  const createBulkOrganisationsMutation = useCreateBulkOrganisations(
    (response: any) => {
      const results: BulkUploadResponse = response.data.data;
      setUploadResults(results);
      
      if (results.failed === 0) {
        showToast('success', `All ${results.successful} organizations created successfully`);
        setIsOpen(false);
        setParsedData([]);
        setFileName('');
        setError('');
        onSuccess?.();
        navigate('/dashboard/organisations');
      } else if (results.successful > 0) {
        showToast('error', `${results.successful} organizations created, ${results.failed} failed`);
      } else {
        showToast('error', `All ${results.failed} organizations failed to create`);
      }
    },
    (error: any) => {
      showToast('error', error.response?.data?.message || 'Failed to create organizations');
      console.error('Bulk create organizations error:', error);
    }
  );


  const parseCSV = (csvText: string): ParsedOrganisation[] => {
    const { data, errors, meta } = Papa.parse<Record<string, string>>(csvText.trim(), {
      header: true,
      skipEmptyLines: true,
    });
  
    if (errors.length) {
      const firstError = errors[0];
      throw new Error(`Error parsing CSV at row ${firstError.row ?? '?'}: ${firstError.message}`);
    }
  
    if (data.length === 0) throw new Error('No data rows found');
  
    const requiredHeaders = ['name', 'email', 'contactPersonName', 'contactPersonEmail'];
    const headers = meta.fields ?? [];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }
  
    const organisations: ParsedOrganisation[] = [];
  
    for (const raw of data) {
      // Trim all values
      const row = Object.fromEntries(
        Object.entries(raw).map(([k, v]) => [k.trim(), (v ?? '').trim()])
      );
  
      const organisation: ParsedOrganisation = {
        name: row.name,
        email: row.email,
        workspaceIds: row.workspaceIds ? row.workspaceIds.split('|').map(s => s.trim()).filter(Boolean) : [],
        contactPerson: {
          name: row.contactPersonName,
          email: row.contactPersonEmail,
        },
        map: [],
      };
  
      // Optional fields
      const optionalFields = [
        'phoneNumber', 'address', 'website', 'linkedInUrl',
        'headOfficeLocation', 'bio', 'mission', 'yearFounded'
      ] as const;
      
      optionalFields.forEach((field) => {
        const value = row[field];
        if (value) (organisation as any)[field] = value;
      });
  
      if (row.contactPersonBio) organisation.contactPerson.bio = row.contactPersonBio;
      if (row.contactPersonLinkedIn) organisation.contactPerson.linkedInUrl = row.contactPersonLinkedIn;
  
      // Categories
      Object.keys(row).forEach(key => {
        if (key.startsWith('category_') && row[key]) {
          const categoryName = key.replace(/^category_/, '').replace(/_/g, ' ').replace(/\\'/g, "'");
          const tags = row[key].split('|').map(t => t.trim()).filter(Boolean);
          if (tags.length > 0) organisation.map.push({ category: categoryName, tags });
        }
      });
  
      organisations.push(organisation);
    }
  
    return organisations.filter(org => org.name && org.email && org.contactPerson.name && org.contactPerson.email);
  };
  
  

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setUploadResults(null);

    setFileName(file.name);
    setError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const parsed = parseCSV(csvText);
        setParsedData(parsed);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse CSV file');
        setParsedData([]);
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (parsedData.length === 0) {
      setError('No valid data found in CSV');
      return;
    }

    try {
      // Use activeWorkspace if not specified in CSV
      const payload = parsedData.map(org => ({
        ...org,
        workspaceIds: org.workspaceIds.length > 0 ? org.workspaceIds : [activeWorkspace || ''],
        yearFounded: org.yearFounded ? new Date(org.yearFounded) : undefined
      }));

      await createBulkOrganisationsMutation.mutateAsync({organisations: payload});
    } catch (error) {
      // Error is handled in the mutation callbacks
    }
  };

  const handleReset = () => {
    setParsedData([]);
    setFileName('');
    setError('');
    setUploadResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      ['name', 'email', 'contactPersonName', 'contactPersonEmail', 'phoneNumber', 'address', 'website', 'linkedInUrl', 'headOfficeLocation', 'bio', 'mission', 'yearFounded', 'contactPersonBio', 'contactPersonLinkedIn', 'category_Area_of_Specialisation', 'category_Funding_Type', 'category_Geographic_Focus', 'category_Sector', 'category_Target_Population', 'category_Organization\'s_Geospatial_Focus'],
      ['ABC Foundation', 'info@abc.org', 'John Doe', 'john@abc.org', '+1234567890', '123 Main St, New York', 'https://abcfoundation.org', 'https://linkedin.com/company/abc-foundation', 'New York, USA', 'Dedicated to improving education and health outcomes', 'Empowering communities through education and healthcare', '2015', 'Education specialist with 10 years experience', 'https://linkedin.com/in/johndoe', 'Education|Health', 'Grants|Private', 'Africa|Europe', 'Non-profit', 'Children|Women', 'Rural|Urban'],
      ['XYZ Group', 'hello@xyz.com', 'Jane Smith', 'jane@xyz.com', '+9876543210', '456 Business Ave, London', 'https://xyzgroup.com', 'https://linkedin.com/company/xyz-group', 'London, UK', 'Leading financial services provider', 'Delivering innovative financial solutions', '2010', 'Financial expert and business strategist', 'https://linkedin.com/in/janesmith', 'Finance', 'Public', 'Asia', 'Financial Services', 'Businesses|Individuals', 'National|International'],
      ['Tech Solutions Inc', 'contact@techsol.com', 'Mike Johnson', 'mike@techsol.com', '+1122334455', '789 Tech Blvd, San Francisco', 'https://techsol.com', 'https://linkedin.com/company/tech-solutions', 'San Francisco, USA', 'Innovative technology company', 'Transforming industries through technology', '2018', 'Tech entrepreneur and innovation leader', 'https://linkedin.com/in/mikejohnson', 'Technology|Innovation', 'Private|Corporate', 'North America|Europe', 'Technology', 'Enterprises|Startups', 'Digital|Hybrid'],
      ['Green Earth NGO', 'info@greenearth.org', 'Sarah Wilson', 'sarah@greenearth.org', '+5566778899', '321 Green Street, Berlin', 'https://greenearth.org', 'https://linkedin.com/company/green-earth', 'Berlin, Germany', 'Environmental conservation organization', 'Protecting our planet for future generations', '2005', 'Environmental activist and conservation expert', 'https://linkedin.com/in/sarahwilson', 'Environment|Climate', 'Grants|Public', 'Global|Asia|Africa', 'Environmental', 'Communities|Governments', 'Terrestrial|Marine']
    ];

    const csvContent = sampleData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample-organizations.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('success', 'Sample CSV downloaded successfully');
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2"
        >
          <Upload className="h-4 w-4" />
          Bulk Upload
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Bulk Upload Organizations
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Instructions Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-3">CSV Format Requirements</h3>
            <div className="text-sm text-blue-700 space-y-4">
              {/* Required Columns */}
              <div>
                <h4 className="font-semibold text-blue-900 mb-1.5">Required columns</h4>
                <p className="mb-1">Must be included exactly as written:</p>
                <p className="font-mono text-xs bg-blue-100 px-2 py-1 rounded inline-block">
                  name, email, contactPersonName, contactPersonEmail
                </p>
              </div>

              {/* Optional Columns */}
              <div>
                <h4 className="font-semibold text-blue-900 mb-1.5">Optional columns</h4>
                <p className="mb-1">
                  phoneNumber, address, website, linkedInUrl, headOfficeLocation, bio, mission, yearFounded, contactPersonBio, contactPersonLinkedIn
                </p>
                <div className="flex items-start gap-2 mt-1.5 p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-800">
                    <strong>⚠️ Do not change column headings.</strong> Renaming or removing columns will cause upload failure.
                  </p>
                </div>
              </div>

              {/* Category Columns */}
              <div>
                <h4 className="font-semibold text-blue-900 mb-1.5">Category columns</h4>
                <ul className="space-y-1 ml-4 list-disc">
                  <li>Must start with <span className="font-mono text-xs bg-blue-100 px-1 rounded">category_</span></li>
                  <li>Use <span className="font-mono text-xs bg-blue-100 px-1 rounded">_</span> instead of spaces</li>
                  <li>Text after <span className="font-mono text-xs bg-blue-100 px-1 rounded">category_</span> is shown as the category name</li>
                </ul>
                <p className="mt-1.5 text-xs italic">
                  Example: <span className="font-mono bg-blue-100 px-1 rounded">category_Area_of_Specialisation</span> → Area of Specialisation
                </p>
              </div>

              {/* Multiple Values */}
              <div>
                <h4 className="font-semibold text-blue-900 mb-1.5">Multiple values</h4>
                <p className="mb-1">Use <span className="font-mono text-xs bg-blue-100 px-1 rounded">|</span> to separate values</p>
                <p className="text-xs italic">
                  (e.g. <span className="font-mono bg-blue-100 px-1 rounded">Policy|Governance|AI</span>)
                </p>
              </div>

              {/* Workspace */}
              <div>
                <h4 className="font-semibold text-blue-900 mb-1.5">Workspace</h4>
                <p>Current workspace is used automatically.</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadSampleCSV}
              className="flex items-center gap-2 mt-3"
            >
              <Download className="h-4 w-4" />
              Download Sample CSV
            </Button>
          </div>

          {/* File Upload Section */}
          <div className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              
              {!fileName ? (
                <div className="space-y-2">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click to upload or drag and drop your CSV file
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose File
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <FileText className="h-8 w-8 mx-auto text-green-600" />
                  <p className="text-sm font-medium">{fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {parsedData.length} organizations found
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change File
                  </Button>
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>

          {/* Preview Section */}
          {parsedData.length > 0 && !uploadResults && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Preview Data</h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {parsedData.map((org, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="mb-3">
                      <h4 className="font-medium text-primary">{org.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {org.email} • {org.contactPerson.name} ({org.contactPerson.email})
                      </p>
                      {org.phoneNumber && (
                        <p className="text-xs text-muted-foreground">Phone: {org.phoneNumber}</p>
                      )}
                      {org.website && (
                        <p className="text-xs text-muted-foreground">Website: {org.website}</p>
                      )}
                      {org.headOfficeLocation && (
                        <p className="text-xs text-muted-foreground">Location: {org.headOfficeLocation}</p>
                      )}
                    </div>
                    
                    {org.map.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Categories:</p>
                        {org.map.map((category, catIndex) => (
                          <div key={catIndex} className="space-y-1">
                            <p className="text-xs font-medium text-blue-600">{category.category}</p>
                            <div className="flex flex-wrap gap-1">
                              {category.tags.map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className="inline-flex items-center rounded-full bg-secondary px-2 py-1 text-xs font-medium"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Results Section */}
          {uploadResults && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold">Upload Results</h3>
                <div className="flex gap-2">
                  {uploadResults.successful > 0 && (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                      {uploadResults.successful} Successful
                    </span>
                  )}
                  {uploadResults.failed > 0 && (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                      {uploadResults.failed} Failed
                    </span>
                  )}
                </div>
              </div>

              {uploadResults.failed > 0 && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-900 mb-3">Failed Organizations</h4>
                    <div className="space-y-3">
                      {uploadResults.results
                        .filter(result => !result.success)
                        .map((result, index) => (
                          <div key={index} className="border border-red-200 rounded-md p-3 bg-white">
                            <div className="flex items-start gap-3">
                              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm">{result.name}</p>
                                <p className="text-xs text-red-600 mt-1">{result.error}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {uploadResults.successful > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-2">Successfully Created</h4>
                  <div className="space-y-1">
                    {uploadResults.results
                      .filter(result => result.success)
                      .map((result, index) => (
                        <p key={index} className="text-sm text-green-700">
                          ✓ {result.name}
                        </p>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={createBulkOrganisationsMutation.isPending}
            >
              {'Reset'}
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={createBulkOrganisationsMutation.isPending}
              >
                {uploadResults ? 'Close' : 'Cancel'}
              </Button>
              {!uploadResults && (
                <Button
                  onClick={handleSubmit}
                  disabled={parsedData.length === 0 || createBulkOrganisationsMutation.isPending}
                  className="bg-primary hover:bg-primary/90"
                >
                  {createBulkOrganisationsMutation.isPending ? 'Creating...' : `Create ${parsedData.length} Organizations`}
                </Button>
              )}
              {uploadResults && uploadResults.successful > 0 && (
                <Button
                  onClick={() => {
                    setIsOpen(false);
                    setParsedData([]);
                    setFileName('');
                    setError('');
                    setUploadResults(null);
                    onSuccess?.();
                    navigate('/dashboard/organisations');
                  }}
                  className="bg-primary hover:bg-primary/90"
                >
                  View Organizations
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}